import React, { type ComponentType, useMemo, memo, useEffect } from 'react';
import { EntryPointContainer, type EntryPointProps, type PreloadedEntryPoint } from 'react-relay';
import { useGlobalScopeEnrolmentEnable } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/global-scope-enrolment-flow/index.tsx';
import {
	setIsGlobalScopeEnrolmentEnabledInLocalStorage,
	getIsGlobalScopeForcedOptOutGinFlagDisabledLocalStorage,
	setGlobalScopeForcedOptOutGinFlagEnabledInLocalStorage,
} from '@atlassian/jira-issue-navigator-global-scope-enrolment/src/services/global-scope-enrolment/local-storage/index.tsx';
import { resetGlobalScopeForcedOptOutGinFlag } from '@atlassian/jira-issue-navigator-global-scope-enrolment/src/services/global-scope-enrolment/set-global-scope-user-preference/index.tsx';
import { GlobalIssueNavigatorSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/global-app.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import type { jqlBuilderWithAiEntryPoint as JqlBuilderWithAiEntryPointType } from '@atlassian/jira-jql-builder-with-ai/src/ui/entrypoint.tsx';

export const EmptySkeleton = () => null;

const GlobalIssueNavigatorEntryPointContainer = memo(
	({
		entryPointReference,
		nativeIssueTableEntryPoint,
		jqlBuilderWithAiEntryPoint,
	}: {
		entryPointReference: PreloadedEntryPoint<AnyEntryPoint>;
		nativeIssueTableEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
		jqlBuilderWithAiEntryPoint?: PreloadedEntryPoint<typeof JqlBuilderWithAiEntryPointType>;
	}) => {
		const entryPointProps = useMemo(
			() => ({
				preloadedEntryPoints: {
					nativeIssueTableEntryPoint,
					...(fg('jql_builder_basic_ssr_stability_fixes') && {
						jqlBuilderWithAiEntryPoint,
					}),
				},
			}),
			[nativeIssueTableEntryPoint, jqlBuilderWithAiEntryPoint],
		);
		const ErrorBoundary = fg('improve_nin_slo_events') ? ReportErrors : JSErrorBoundary;

		return (
			<UFOSegment name="jira-router-gin-redirect-entrypoint">
				<ErrorBoundary
					id="jira-router-gin-redirect-entrypoint"
					packageName="jiraRouterIssueNavigatorEntries"
				>
					<EntryPointReferencePlaceholder
						name="gin-redirect-entrypoint"
						fallback={<EmptySkeleton />}
						entryPointReference={entryPointReference}
					>
						<EntryPointContainer
							entryPointReference={entryPointReference}
							// EntryPointContainer can't infer the type of props from entryPointReference correctly
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							props={entryPointProps as never}
						/>
					</EntryPointReferencePlaceholder>
				</ErrorBoundary>
			</UFOSegment>
		);
	},
);

type NestedEntryPoints = {
	globalIssueNavigatorEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
	nativeIssueTableEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
	jqlBuilderWithAiEntryPoint?: PreloadedEntryPoint<typeof JqlBuilderWithAiEntryPointType>;
};

/**
 * GinRedirect is responsible for deciding whether to render NIN in the global scope, or to redirect to legacy GIN. It
 * relies on two sources of truth:
 *
 * 1. Local storage. This is used as a cache to improve performance, and is prefered whenever it is available.
 *
 * 2. Jira's backend. This is used as a backup if local storage is unavailable, and is used to keep local storage
 *    up-to-date.
 */
export const GinRedirect = ({
	preloadedEntryPoints,
}: {
	preloadedEntryPoints: NestedEntryPoints;
}): React.JSX.Element => {
	const [isNinGlobalScopeEnrolmentEnabled, resourceData] = useGlobalScopeEnrolmentEnable();
	const isAnonymous = useIsAnonymous();
	// Regardless of the existing localStorageData value, we update the localStorage value once we get resourceData so
	// that future renders are fast and accurate.
	useEffect(() => {
		if (resourceData !== null && resourceData !== undefined) {
			setIsGlobalScopeEnrolmentEnabledInLocalStorage(resourceData);
		}
	}, [resourceData]);

	// If NIN in global scope is not enabled then we redirect to legacy GIN by performing a reload
	useEffect(() => {
		if (isNinGlobalScopeEnrolmentEnabled === false) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.reload();
		}
	}, [isNinGlobalScopeEnrolmentEnabled]);

	useEffect(() => {
		if (isNinGlobalScopeEnrolmentEnabled) {
			// If we are in global scope nin then reset/enable the forced opt out flag for GIN if we haven't done it already
			const globalGINFlagDisabled = getIsGlobalScopeForcedOptOutGinFlagDisabledLocalStorage();
			if (globalGINFlagDisabled !== false) {
				!isAnonymous && resetGlobalScopeForcedOptOutGinFlag();
				setGlobalScopeForcedOptOutGinFlagEnabledInLocalStorage();
			}
		}
	}, [isAnonymous, isNinGlobalScopeEnrolmentEnabled]);

	const entryPointReference = preloadedEntryPoints.globalIssueNavigatorEntryPoint;
	const nativeIssueTableEntryPoint = preloadedEntryPoints.nativeIssueTableEntryPoint;
	const jqlBuilderWithAiEntryPoint = preloadedEntryPoints.jqlBuilderWithAiEntryPoint;

	// If NIN in global scope is enabled then we render it. If NIN in global scope is not enabled, or we're blocked
	// waiting on the resource to load then we render GlobalIssueNavigatorSkeleton.
	return isNinGlobalScopeEnrolmentEnabled === true && entryPointReference ? (
		<GlobalIssueNavigatorEntryPointContainer
			entryPointReference={entryPointReference}
			nativeIssueTableEntryPoint={nativeIssueTableEntryPoint}
			jqlBuilderWithAiEntryPoint={jqlBuilderWithAiEntryPoint}
		/>
	) : (
		<GlobalIssueNavigatorSkeleton />
	);
};

export const GinRedirectEntryPoint: ComponentType<EntryPointProps<{}, {}, {}, {}>> = ({
	entryPoints,
}) => <GinRedirect preloadedEntryPoints={entryPoints} />;

export default GinRedirectEntryPoint;
